import React, { useState } from "react";
import {
    Box,
    Grid,
    TextField,
    InputAdornment,
    Typography,
    Button,
    IconButton
} from "@mui/material";
import InfoButton from "../../Tooltip/InfoTextField";
import { PaymentProviderConfigViewModel } from "../../../Types/ExportTypes";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../Context/Contexts";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

type Props = {
    viewModel: PaymentProviderConfigViewModel | null;
    onSave: (vm: PaymentProviderConfigViewModel) => void;
};

const PayPalPaymentProviderWizard: React.FC<Props> = ({ viewModel, onSave }) => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const [localViewModel, setLocalViewModel] = useState<PaymentProviderConfigViewModel>(viewModel ?? {});
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (field: keyof PaymentProviderConfigViewModel, value: string | null) => {
        setLocalViewModel((prev) => ({ ...prev, [field]: value }));
    };

    const handleFinish = () => {
        if (!validateViewModel()) return;
        onSave(localViewModel);
    };

    const validateViewModel= () => {
        if (!localViewModel.description?.trim()) {
            showSnackbar(t("help.descriptionRequired"), "warning");
            return false;
        }

        if (!localViewModel.paymentProviderInfo1?.trim()) {
            showSnackbar(t("help.paypalApiUrlRequired"), "warning");
            return false;
        }

        if (!localViewModel.paymentProviderInfo2?.trim()) {
            showSnackbar(t("help.paypalApiUsernameRequired"), "warning");
            return false;
        }

        if (!localViewModel.paymentProviderInfo3?.trim()) {
            showSnackbar(t("help.paypalApiPasswordRequired"), "warning");
            return false;
        }

        if (!localViewModel.paymentProviderInfo4?.trim()) {
            showSnackbar(t("help.paypalApiSignatureRequired"), "warning");
            return false;
        }
    }

    const cancelWizard = () => {
        window.history.back();
    };

    return (
        <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", px: 2 }}>
            <Typography variant="h6" gutterBottom>
                PayPal
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label={t("common.description")}
                        value={localViewModel.description || ""}
                        onChange={(e) => handleChange("description", e.target.value)}
                        fullWidth
                        margin="normal"
                        error={!localViewModel.description?.trim()}
                        helperText={
                            !localViewModel.description?.trim() ? t("help.descriptionRequired") : ""
                        }
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="PayPal API URL"
                        value={localViewModel.paymentProviderInfo1 || ""}
                        onChange={(e) => handleChange("paymentProviderInfo1", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.paypalApiUrl")} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={t("common.paypalApiUsername")}
                        value={localViewModel.paymentProviderInfo2 || ""}
                        onChange={(e) => handleChange("paymentProviderInfo2", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.paypalApiUsername")} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={t("common.paypalApiPassword")}
                        value={localViewModel.paymentProviderInfo3 || ""}
                        onChange={(e) => handleChange("paymentProviderInfo3", e.target.value)}
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <InfoButton infoText={t("help.paypalApiPassword")} />
                                        </Box>
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            size="small"
                                            sx={{ padding: 0.5 }}
                                        >
                                            {showPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                                        </IconButton>
                                    </Box>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label={t("common.paypalApiSignature")}
                        value={localViewModel.paymentProviderInfo4 || ""}
                        onChange={(e) => handleChange("paymentProviderInfo4", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <InfoButton infoText={t("help.paypalApiSignature")} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
            </Grid>

            <Box sx={{ marginTop: 2, textAlign: "center" }}>
                <Button variant="contained" color="primary" onClick={handleFinish} sx={{ mr: 2 }}>
                    {t("common.finish")}
                </Button>
                <Button variant="contained" color="warning" onClick={cancelWizard}>
                    {t("common.cancelWizard")}
                </Button>
            </Box>
        </Box>
    );
};

export default PayPalPaymentProviderWizard;
